import {
  Modal as ChakraModal,
  IconButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react"
import { useIntl } from "react-intl"

import { MultiplyIcon } from "@/components/Icons"
import { H4 } from "@/components/Typographies"

type Props = React.PropsWithChildren<{
  isOpen: boolean
  onClose: () => void
  title: React.ReactNode
  size?: string
  scrollBehavior?: "inside" | "outside"
}>

// Shared dialog (ADR 0001, lot 4): white, a clear header closed by a red line across its whole
// width (graphic accent of the brand book), 24px paddings. The body and footer paddings come from
// the theme (modal-definition.ts), so ModalBody / ModalFooter must be used for the content.
export const Modal: React.FC<Props> = ({
  isOpen,
  onClose,
  title,
  children,
  size = "2xl",
  scrollBehavior = "inside",
}) => {
  const intl = useIntl()

  return (
    <ChakraModal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior={scrollBehavior}
      size={size}
    >
      <ModalOverlay />
      <ModalContent borderRadius={0} display="flex" flexDir="column" bg="surface">
        <ModalHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap="6"
          px="6"
          py="5"
          // Red line across the whole header: it separates the title from a scrolling body.
          borderBottom="2px solid"
          borderColor="accent"
        >
          <H4 fontSize="xl">{title}</H4>
          {/* The cross alone; "Close" stays its accessible name. */}
          <IconButton
            variant="transparent"
            aria-label={intl.formatMessage({ id: "close" })}
            icon={<MultiplyIcon boxSize={5} />}
            onClick={onClose}
            flexShrink={0}
            // No button minimum height (48px in the theme): it stretched the header under the title.
            minW="auto"
            minH="auto"
            h="auto"
            p="0"
            color="text"
            _hover={{ color: "accent" }}
          />
        </ModalHeader>
        {children}
      </ModalContent>
    </ChakraModal>
  )
}
